import * as React from "react";
import {
    CommonStringField,
    commonStringFieldCodec,
    EventId,
    raceNumberFieldCodec,
    raceTable,
    RegistrationField,
} from "../../../domain/event";
import { useQuery } from "../../../utils/supabase/useQuery";
import { constNull, pipe } from "fp-ts/function";
import { array, eq, nonEmptyArray, option, string } from "fp-ts";
import * as t from "io-ts";
import { SupabaseClient } from "@supabase/supabase-js";
import { registrationTable } from "../../../domain/registration";
import { column, columns, join } from "../../../utils/pg-ts/pg-ts";
import { athleteMessages, athleteTable } from "../../../domain/user";
import { categoriesTable } from "../../../domain/category";
import { emptyArray, pick } from "../../../utils/fp";
import { nullable } from "../../../domain/common";
import { isCompletedState } from "../../../utils/supabase/QueryState";
import { DialogButton, useDialogState } from "../../common/DialogButton";
import { CancelableDialogBody } from "../../user/CancelableDialogBody";
import { useTranslation } from "../../../utils/i18n/lang";
import { GridColumns } from "@mui/x-data-grid/models/colDef/gridColDef";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { DataTable } from "../../DataTable";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { EventDetailRace } from "../../../hooks/event";
import { assertUnreachable } from "../../../utils/assertUnreachable";
import { Eq } from "fp-ts/Eq";
import { raceNumberOrd, resultMessages } from "../../../domain/result";
import { RegistrationStateView } from "../../xathlo/RegistrationState";

interface Props extends Pick<RegistrationDataTableProps, "races"> {
    eventId: EventId;
}

export const StartListBtn = (p: Props) => {
    const q = usePublicOnlineRegistrationsQuery(p.eventId);
    const dialogState = useDialogState();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    React.useEffect(() => {
        if (dialogState.isOpen) {
            q.mutate();
        }
    }, [dialogState.isOpen]);

    return pipe(
        q.state,
        option.fromPredicate(isCompletedState),
        option.chain((state) => nonEmptyArray.fromArray(state.data)),
        option.fold(constNull, (registrations) => {
            return (
                <DialogButton
                    state={dialogState}
                    btn={{
                        variant: "text",
                        children: `seznam registrovaných`,
                    }}
                    dialog={{
                        maxWidth: "md",
                        fullWidth: true,
                        fullScreen: isMobile,
                        children: (
                            <CancelableDialogBody
                                onCancel={dialogState.close}
                                cancelBtnLabel={"zavřít"}
                            >
                                <RegistrationDataTable
                                    isMobile={isMobile}
                                    rows={registrations}
                                    races={p.races}
                                />
                            </CancelableDialogBody>
                        ),
                    }}
                />
            );
        })
    );
};

const registrationCols = pipe(
    registrationTable.columns,
    pick("id", "club", "race_id", "category_id", "number", "state")
);
const raceCols = pipe(raceTable.columns, pick("event_id", "title"));
const raceAlias = "race";
const athleteCols = pipe(athleteTable.columns, pick("name", "id"));
const athleteAlias = "athlete";
const categoryCols = pipe(
    categoriesTable.columns,
    pick("title", "description")
);
const categoryAlias = "category";

const itemCodec = t.type(
    {
        ...registrationCols,
        [raceAlias]: t.type(raceCols),
        [athleteAlias]: t.type(athleteCols),
        [categoryAlias]: nullable(t.type(categoryCols)),
    },
    "OnlineRegistration"
);
export type OnlineRegistration = t.TypeOf<typeof itemCodec>;

const usePublicOnlineRegistrationsQuery = (eventId: EventId) =>
    useQuery(
        `onlineRegistrations_${eventId}`,
        publicOnlineRegistrationsQuery(eventId),
        t.array(itemCodec)
    );
const publicOnlineRegistrationsQuery = (eventId: EventId) => {
    return (supabase: SupabaseClient) =>
        supabase
            .from(registrationTable.name)
            .select(
                columns(
                    registrationCols,
                    ...[
                        join(raceTable, raceCols, raceAlias, true),
                        join(athleteTable, athleteCols, athleteAlias),
                        join(categoriesTable, categoryCols, categoryAlias),
                    ]
                )
            )
            .eq(`${raceAlias}.${column(raceTable, "event_id")}`, eventId);
};
interface TableRow extends OnlineRegistration {}

interface RegistrationDataTableProps {
    rows: NonEmptyArray<TableRow>;
    races: Array<EventDetailRace>;
    isMobile: boolean;
}
const RegistrationDataTable = (p: RegistrationDataTableProps) => {
    const athleteM = useTranslation(athleteMessages);
    const resultM = useTranslation(resultMessages);

    const cols = React.useMemo((): GridColumns<TableRow> => {
        const fixed: GridColumns<TableRow> = [
            {
                ...columnCommon,
                field: "name",
                headerName: athleteM.name,
                valueGetter: (p) => p.row.athlete.name,
            },
            {
                ...columnCommon,
                field: "race",
                headerName: resultM.race,
                valueGetter: (p) => p.row.race.title,
            },
            {
                ...columnCommon,
                field: "category",
                flex: 0.5,
                headerName: resultM.category,
                valueGetter: (p) =>
                    pipe(
                        p.row.category,
                        option.fromNullable,
                        option.map((cat) => cat.title),
                        option.toNullable
                    ),
            },
            {
                ...columnCommon,
                field: "club",
                flex: 1.5,
                headerName: athleteM.club,
            },
            {
                ...columnCommon,
                field: "state",
                flex: 1.5,
                headerName: "status",
                renderCell: (p) => (
                    <RegistrationStateView state={p.row.state} />
                ),
            },
        ];

        const dynamics: GridColumns<TableRow> = pipe(
            p.races,
            array.chain((race) =>
                pipe(
                    race.registration_fields,
                    option.fromNullable,
                    option.getOrElse(emptyArray<RegistrationField>())
                )
            ),
            array.uniq(registrationFieldEqForTableCols),
            array.map((field) => {
                switch (field.kind) {
                    case "raceNumber":
                        return {
                            ...columnCommon,
                            field: "number",
                            headerName: resultM.number,
                            sortComparator: raceNumberOrd.compare,
                        };

                    case "commonString":
                        return {
                            ...columnCommon,
                            // TODO other fields
                            field: field.label,
                        };
                }
                assertUnreachable(field);
            })
        );

        return pipe(fixed, array.concat(dynamics));
    }, [athleteM, p.races]);

    return (
        <Box sx={{ height: "80vh" }}>
            <DataTable
                columns={cols}
                rows={p.rows}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "name", sort: "asc" }],
                    },
                    columns: {
                        columnVisibilityModel: {
                            club: !p.isMobile,
                            race: false,
                            category: !p.isMobile,
                        },
                    },
                }}
            />
        </Box>
    );
};
const columnCommon = {
    flex: 1,
};

const commonStringFieldByLabelEq = pipe(
    string.Eq,
    eq.contramap((field: CommonStringField) => field.label)
);
// weak equality... does not care about required/non required etc
const registrationFieldEqForTableCols: Eq<RegistrationField> = {
    equals: (x, y) => {
        switch (x.kind) {
            case "commonString":
                return commonStringFieldCodec.is(y)
                    ? commonStringFieldByLabelEq.equals(x, y)
                    : false;

            case "raceNumber":
                return raceNumberFieldCodec.is(y); // no more checks needed
        }

        assertUnreachable(x);
    },
};
